import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabase';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';
import { useUnloggedCount } from '../contexts/UnloggedCountContext';
import { Icon } from './Icon.js';
import ChecklistLogger from './ChecklistLogger';
import FeedbackBox from './FeedbackBox';
import './BottomNavigation.css';

const BottomNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // State for checklist
  const [isChecklistOpen, setChecklistOpen] = useState(false);
  const { count: unloggedCount, refresh: refreshUnloggedCount } = useUnloggedCount();
  
  // State for feedback
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  
  // State for onboarding notification on Insights tab
  const [showInsightsNotification, setShowInsightsNotification] = useState(false);
  
  const [userProfile, setUserProfile] = useState(() => {
    try {
      const cached = sessionStorage.getItem('current_user_profile');
      if (cached) {
        const parsed = JSON.parse(cached);
        // Ensure avatar_url exists if profilePictureUrl exists
        if (!parsed.avatar_url && parsed.profilePictureUrl) {
          parsed.avatar_url = parsed.profilePictureUrl;
        }
        return parsed;
      }
    } catch (e) {
      // Ignore error
    }
    return null;
  });
  
  // Determine active page from location
  const getActivePage = () => {
    const path = location.pathname;
    if (path === '/home' || path === '/dashboard') return 'home';
    if (path === '/insights') return 'insights';
    if (path.startsWith('/protocols/create')) return 'create-protocol';
    if (path === '/messages') return 'messages';
    if (path.startsWith('/user')) return 'user';
    if (path === '/checklist') return 'checklist';
    if (path === '/feedback') return 'feedback';
    return 'home';
  };
  
  const currentPage = getActivePage();

  // Fetch user profile
  const fetchUserProfile = async (forceRefresh = false) => {
    try {
      // Use getSession() to avoid network requests
      const { data: { session } = {} } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) {
        setUserProfile(null);
        return;
      }

      // Check cache first (unless force refresh)
      if (!forceRefresh) {
        const cachedProfile = sessionStorage.getItem(`userProfile_${user.id}`);
        if (cachedProfile) {
          try {
            const parsedProfile = JSON.parse(cachedProfile);
            // Ensure avatar_url is populated for display
            if (!parsedProfile.avatar_url && parsedProfile.profilePictureUrl) {
              parsedProfile.avatar_url = parsedProfile.profilePictureUrl;
            }
            
            // Check if cached profile has avatar_url
            if (parsedProfile.avatar_url || parsedProfile.profile_picture_url) {
              setUserProfile(parsedProfile);
              // Update current_user_profile to keep it fresh
              sessionStorage.setItem('current_user_profile', JSON.stringify(parsedProfile));
              return;
            }
          } catch (e) {
            // Invalid cache, continue to fetch
          }
        }
      }

      // Fetch from database - use deduplication to prevent duplicate requests
      const cacheKey = getCacheKey('fetchUserProfile', user.id);
      const profileData = await dedupeRequest(cacheKey, async () => {
        const { data } = await supabase
          .from('user_profiles')
          .select('username, profile_picture_url, gender')
          .eq('user_id', user.id)
          .maybeSingle();
        return data;
      }, { keepAliveMs: 2000, caller: 'BottomNavigation' });

      if (profileData) {
        const profile = {
          id: user.id,
          username: profileData.username || 'User',
          profile_picture_url: profileData.profile_picture_url,
          avatar_url: profileData.profile_picture_url, // Add alias for consistency
          profilePictureUrl: profileData.profile_picture_url || '', // Add camelCase for useUserProfile hook
          gender: profileData.gender || null
        };
        setUserProfile(profile);
        // Store with same structure as Home.js for consistency, but ensure avatar_url is present
        const cacheData = {
          id: user.id,
          username: profileData.username || '',
          profilePictureUrl: profileData.profile_picture_url || '',
          avatar_url: profileData.profile_picture_url || '', 
          gender: profileData.gender || null
        };
        sessionStorage.setItem(`userProfile_${user.id}`, JSON.stringify(cacheData));
        sessionStorage.setItem('current_user_profile', JSON.stringify(cacheData));
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
      setUserProfile(null);
    }
  };

  // Check for onboarding notification on Insights tab
  useEffect(() => {
    const checkInsightsNotification = () => {
      const hasSeenBiohacksOnboarding = sessionStorage.getItem('seen_biohacks_onboarding');
      const hasSeenInsightsOnboarding = sessionStorage.getItem('seen_insights_onboarding');
      
      // Show notification if user has seen biohacks onboarding but not insights onboarding
      if (hasSeenBiohacksOnboarding && !hasSeenInsightsOnboarding) {
        setShowInsightsNotification(true);
      } else {
        setShowInsightsNotification(false);
      }
    };
    
    // Check initially
    checkInsightsNotification();
    
    // Listen for custom event when onboarding progress updates
    const handleOnboardingUpdate = () => {
      checkInsightsNotification();
    };
    
    // Listen for storage changes (when user closes biohacks popup or sees insights onboarding)
    const handleStorageChange = (e) => {
      if (e.key === 'seen_biohacks_onboarding' || e.key === 'seen_insights_onboarding') {
        checkInsightsNotification();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('onboardingProgressUpdate', handleOnboardingUpdate);
    
    // Also check periodically in case sessionStorage is updated in same window
    const interval = setInterval(checkInsightsNotification, 500);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('onboardingProgressUpdate', handleOnboardingUpdate);
      clearInterval(interval);
    };
  }, []);
  
  useEffect(() => {
    fetchUserProfile(false);
    
    // Refresh profile when component becomes visible (unlogged count handled by UnloggedCountProvider)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchUserProfile();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Listen for profile updates
  useEffect(() => {
    const channel = supabase
      .channel('profile_updates_bottom_nav')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'user_profiles'
      }, () => {
        fetchUserProfile();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // When checklist closes, refresh count
  const handleChecklistClose = () => {
    setChecklistOpen(false);
    refreshUnloggedCount();
  };

  const handleNavClick = (e, path) => {
    e.preventDefault();
    e.stopPropagation();
    
    // On desktop only, close modals (on mobile these are pages, not modals)
    if (window.innerWidth > 480) {
      setChecklistOpen(false);
      setIsFeedbackOpen(false);
    }
    
    // Navigate to the specified path
    navigate(path);
  };

  return (
    <>
      <nav className="bottom-navigation">
        <div className="bottom-nav-container">
          {/* Home Button */}
          <button 
            className={`bottom-nav-icon ${currentPage === 'home' ? 'active' : ''}`}
            onClick={(e) => handleNavClick(e, '/home')}
            title="Home"
            aria-label="Home"
            aria-current={currentPage === 'home' ? 'page' : undefined}
          >
            <Icon name="Home" className="icon--md" />
          </button>
          
          {/* Checklist Button */}
          <button 
            className={`bottom-nav-icon ${currentPage === 'checklist' ? 'active' : ''}`}
            title="Daily Checklist" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              
              // On mobile, always navigate to page (never open modal)
              if (window.innerWidth <= 480) {
                navigate('/checklist');
              } else {
                // On desktop, open modal
                setIsFeedbackOpen(false);
                setChecklistOpen(true);
              }
            }} 
            style={{ position: 'relative' }}
            aria-label={unloggedCount > 0 ? `Daily checklist with ${unloggedCount} pending items` : 'Daily checklist'}
            aria-current={currentPage === 'checklist' ? 'page' : undefined}
          >
            <Icon name="CheckSquare" className="icon--md" />
            {unloggedCount > 0 && (
              <span className="bottom-nav-badge" aria-label={`${unloggedCount} unlogged hacks`}>
                {unloggedCount}
              </span>
            )}
          </button>
          
          <button
            className={`bottom-nav-icon bottom-nav-icon--create-protocol ${currentPage === 'create-protocol' ? 'active' : ''}`}
            onClick={(e) => handleNavClick(e, '/protocols/create')}
            title="Create Protocol"
            aria-label="Create a new protocol"
            aria-current={currentPage === 'create-protocol' ? 'page' : undefined}
          >
            <Icon name="Plus" className="icon--md" />
          </button>
          
          <button 
            className={`bottom-nav-icon ${currentPage === 'insights' ? 'active' : ''}`}
            onClick={(e) => handleNavClick(e, '/insights')}
            title="Insights"
            aria-label={showInsightsNotification ? "Insights - New onboarding step available" : "Insights"}
            aria-current={currentPage === 'insights' ? 'page' : undefined}
            data-onboarding="insights"
            style={{ position: 'relative' }}
          >
            <Icon name="Lightbulb" className="icon--md" />
            {showInsightsNotification && (
              <span 
                className="bottom-nav-badge" 
                style={{ 
                  background: 'var(--color-primary, #007bff)',
                  width: '8px',
                  height: '8px',
                  top: '6px',
                  right: '6px',
                  fontSize: '0',
                  boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.9)'
                }}
                aria-label="New onboarding step available"
              />
            )}
          </button>
          
          {/* Profile Button */}
          <button
            className={`bottom-nav-icon nav-profile-btn${currentPage === 'user' ? ' active' : ''}`}
            aria-label="User profile"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              
              // On desktop only, close modals (on mobile these are pages)
              if (window.innerWidth > 480) {
                setChecklistOpen(false);
                setIsFeedbackOpen(false);
              }
              
              // Navigate to user profile protocols page
              navigate('/user#protocols');
            }}
            title={userProfile?.username || 'User Profile'}
            data-onboarding="profile"
          >
            {userProfile?.avatar_url && userProfile.avatar_url !== '/default-avatar.jpg' ? (
              <img 
                src={userProfile.avatar_url} 
                alt={`${userProfile.username}'s profile`}
                className="nav-profile-image"
                style={{ width: '28px', height: '28px' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div 
              className="nav-profile-fallback"
              style={{ 
                display: userProfile?.avatar_url && userProfile.avatar_url !== '/default-avatar.jpg' ? 'none' : 'flex',
                width: '28px', height: '28px', fontSize: '14px'
              }}
            >
              {(userProfile?.username || 'U').charAt(0).toUpperCase()}
            </div>
          </button>
          
          {/* Feedback Button */}
          <button
            className={`bottom-nav-icon bottom-nav-icon--feedback ${currentPage === 'feedback' ? 'active' : ''}`}
            title="Feedback & Suggestions"
            aria-label="Submit feedback or suggestions"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              
              // On mobile, always navigate to page (never open modal)
              if (window.innerWidth <= 480) {
                navigate('/feedback');
              } else {
                // On desktop, open modal
                setChecklistOpen(false);
                setIsFeedbackOpen(true);
              }
            }}
            aria-current={currentPage === 'feedback' ? 'page' : undefined}
          >
            <Icon name="Megaphone" className="icon--md" />
          </button>
        </div>
      </nav>
      
      {/* ChecklistLogger panel - Desktop only (on mobile it's a full page) */}
      {isChecklistOpen && window.innerWidth > 480 && (
        <ChecklistLogger 
          open={isChecklistOpen} 
          onClose={handleChecklistClose} 
          onLogChange={refreshUnloggedCount} 
        />
      )}
      
      {/* Feedback Box Modal - Desktop only (on mobile it's a full page) */}
      {window.innerWidth > 480 && (
        <FeedbackBox isOpen={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} />
      )}
    </>
  );
};

export default BottomNavigation;